import { Directive, ElementRef, HostListener, OnDestroy } from '@angular/core';
import { FormGroupDirective } from '@angular/forms';

@Directive({
	selector: '[appInvalidFormScroll]'
})
export class InvalidFormScrollDirective implements OnDestroy {
	private static readonly FOCUS_DELAY_MS = 350;
	private static readonly HIGHLIGHT_DURATION_MS = 2500;

	private readonly invalidFieldSelector =
		'input.ng-invalid, textarea.ng-invalid, select.ng-invalid, .mat-input-element.ng-invalid, mat-select.ng-invalid, .mat-select.ng-invalid, mat-radio-group.ng-invalid, mat-slide-toggle.ng-invalid, [matinput].ng-invalid';
	private readonly invalidContainerSelector =
		'.mat-form-field.ng-invalid, mat-form-field.ng-invalid, .mat-select.ng-invalid, mat-select.ng-invalid, mat-radio-group.ng-invalid, mat-slide-toggle.ng-invalid';
	private readonly nestedFocusableSelector = `${this.invalidFieldSelector}, .mat-select-trigger, .mat-slide-toggle-input`;
	private readonly highlightClassName = 'invalid-form-scroll-target';
	private clearHighlightTimeoutId?: number;
	private focusTimeoutId?: number;
	private highlightedElement?: HTMLElement;

	constructor(private elementRef: ElementRef, private formGroupDirective: FormGroupDirective) {}

	ngOnDestroy(): void {
		this.clearHighlight();
		this.clearFocusTimeout();
	}

	@HostListener('ngSubmit') onSubmit() {
		this.formGroupDirective.control.invalid && this.scrollToFirstInvalidControl();
	}

	private scrollToFirstInvalidControl() {
		const firstInvalidControl = this.findFirstInvalidControl();

		if (!firstInvalidControl) {
			return;
		}

		const visualTarget = this.getVisualTarget(firstInvalidControl);
		const focusTarget = this.getFocusableTarget(firstInvalidControl);

		this.highlightTarget(visualTarget);

		window.scroll({
			top: this.getTopOffset(visualTarget),
			left: 0,
			behavior: 'smooth'
		});

		this.clearFocusTimeout();
		this.focusTimeoutId = window.setTimeout(
			() => this.focusTarget(focusTarget),
			InvalidFormScrollDirective.FOCUS_DELAY_MS
		);
	}

	private findFirstInvalidControl(): HTMLElement | null {
		const hostElement = this.elementRef.nativeElement as HTMLElement;
		const directInvalidControl = hostElement.querySelector(this.invalidFieldSelector) as HTMLElement | null;

		if (directInvalidControl) {
			return directInvalidControl;
		}

		const invalidContainers = Array.from(hostElement.querySelectorAll('.ng-invalid')) as HTMLElement[];

		for (const invalidContainer of invalidContainers) {
			if (invalidContainer.matches('form')) {
				continue;
			}

			const nestedInvalidControl = invalidContainer.querySelector(this.invalidFieldSelector) as HTMLElement | null;

			if (nestedInvalidControl) {
				return nestedInvalidControl;
			}

			const nestedInvalidContainer = invalidContainer.querySelector(
				this.invalidContainerSelector
			) as HTMLElement | null;

			if (nestedInvalidContainer) {
				return nestedInvalidContainer;
			}

			const nestedFocusableControl = invalidContainer.querySelector(this.nestedFocusableSelector) as HTMLElement | null;

			if (nestedFocusableControl) {
				return nestedFocusableControl;
			}
		}

		return null;
	}

	private getVisualTarget(controlEl: HTMLElement): HTMLElement {
		return (
			(controlEl.closest(
				'mat-form-field, .mat-form-field, .mat-mdc-form-field, mat-slide-toggle, mat-radio-group'
			) as HTMLElement | null) || controlEl
		);
	}

	private getFocusableTarget(controlEl: HTMLElement): HTMLElement {
		return (
			(controlEl.matches('input, textarea, select, button, [tabindex]')
				? controlEl
				: (controlEl.querySelector(
						'input, textarea, select, button, [tabindex], .mat-select-trigger'
				  ) as HTMLElement | null)) || controlEl
		);
	}

	private focusTarget(target: HTMLElement): void {
		if (!target) {
			return;
		}

		try {
			target.focus({ preventScroll: true });
		} catch {
			target.focus();
		}
	}

	private highlightTarget(target: HTMLElement): void {
		this.clearHighlight();
		target.classList.add(this.highlightClassName);
		this.highlightedElement = target;
		this.clearHighlightTimeoutId = window.setTimeout(
			() => this.clearHighlight(),
			InvalidFormScrollDirective.HIGHLIGHT_DURATION_MS
		);
	}

	private clearHighlight(): void {
		if (this.clearHighlightTimeoutId) {
			window.clearTimeout(this.clearHighlightTimeoutId);
			this.clearHighlightTimeoutId = undefined;
		}

		this.highlightedElement?.classList.remove(this.highlightClassName);
		this.highlightedElement = undefined;
	}

	private clearFocusTimeout(): void {
		if (this.focusTimeoutId) {
			window.clearTimeout(this.focusTimeoutId);
			this.focusTimeoutId = undefined;
		}
	}

	private getTopOffset(controlEl: HTMLElement): number {
		const labelOffset = 100;
		return Math.max(controlEl.getBoundingClientRect().top + window.scrollY - labelOffset, 0);
	}
}
